import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  Circle,
  useMap,
} from "react-leaflet";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Navigation, Route, Locate, RefreshCw } from "lucide-react";

// ---------- pin factory ----------------------------------------------------
function pinIcon(letter, color = "#0055FF") {
  const safe = String(letter).replace(/[<>&"']/g, "");
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46"
      style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.25))">
      <path d="M18 0C8 0 0 8 0 18c0 13 18 28 18 28s18-15 18-28C36 8 28 0 18 0z" fill="${color}"/>
      <circle cx="18" cy="18" r="11" fill="white"/>
      <text x="18" y="23" font-family="Manrope, sans-serif" font-size="14" font-weight="700"
        text-anchor="middle" fill="${color}">${safe}</text>
    </svg>`;
  return L.divIcon({
    className: "dialflow-pin",
    html: svg,
    iconSize: [36, 46],
    iconAnchor: [18, 44],
    popupAnchor: [0, -40],
  });
}

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    map.flyTo(center, zoom, { duration: 0.6 });
  }, [center, zoom, map]);
  return null;
}

function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points.length]);
  return null;
}

function fmtAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))}s ago`;
    if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
    if (ms < 48 * 3_600_000) return `${Math.round(ms / 3_600_000)}h ago`;
    return `${Math.round(ms / 86_400_000)} days ago`;
  } catch {
    return "";
  }
}

// The employee app sends a fix every few seconds while it's open; nothing for
// 10 minutes means the app is closed or the phone has no signal.
const LIVE_MS = 10 * 60_000;
const isLive = (l) => Date.now() - new Date(l.captured_at).getTime() < LIVE_MS;
const OFFLINE = "#94a3b8";
// Wi-Fi/cell-tower positions can be kilometres off; GPS is within ~50 m.
const ROUGH_M = 100;

export default function MapPage() {
  const [locations, setLocations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [trail, setTrail] = useState([]);
  const [flyTarget, setFlyTarget] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshTimer = useRef(null);

  const load = async () => {
    const { data } = await api.get("/locations");
    setLocations(data);
    if (!selectedId && data[0]) setSelectedId(data[0].employee_id);
  };

  const loadTrail = async (empId) => {
    if (!empId) return setTrail([]);
    try {
      const { data } = await api.get(`/locations/history?employee_id=${empId}&limit=200`);
      setTrail(data);
    } catch {
      setTrail([]);
    }
  };

  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    loadTrail(selectedId);
  }, [selectedId]);

  useEffect(() => {
    if (!autoRefresh) {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
      refreshTimer.current = null;
      return;
    }
    refreshTimer.current = setInterval(() => {
      load();
      if (selectedId) loadTrail(selectedId);
    }, 20_000);
    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedId]);

  const center = useMemo(() => {
    if (locations.length === 0) return [12.9716, 77.5946];
    const active = locations.find((l) => l.employee_id === selectedId) || locations[0];
    return [active.lat, active.lng];
  }, [locations, selectedId]);

  const liveCount = locations.filter(isLive).length;
  const allPoints = useMemo(() => {
    const live = locations.filter(isLive);
    return (live.length ? live : locations).map((l) => [l.lat, l.lng]);
  }, [locations]);

  const selected = locations.find((l) => l.employee_id === selectedId);
  const trailLatLngs = trail.map((p) => [p.lat, p.lng]);

  const totalKm = useMemo(() => {
    if (trailLatLngs.length < 2) return 0;
    let m = 0;
    for (let i = 1; i < trailLatLngs.length; i++) {
      const a = L.latLng(trailLatLngs[i - 1]);
      const b = L.latLng(trailLatLngs[i]);
      m += a.distanceTo(b);
    }
    return m / 1000;
  }, [trailLatLngs]);

  const focusOn = (l) => {
    setSelectedId(l.employee_id);
    setFlyTarget([l.lat, l.lng]);
  };

  const openInGoogleMaps = () => {
    if (!selected) return;
    window.open(
      `https://www.google.com/maps?q=${selected.lat},${selected.lng}`,
      "_blank"
    );
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="owner-map">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
            Field
          </div>
          <h1 className="font-heading text-3xl font-bold mt-2">Live map</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Live while the employee app is open on their phone. Grey pins are
            offline (last known place). Click a teammate to see their trail.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="mono text-xs text-slate-500 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FF5000] animate-pulse" />
            {liveCount} live
            {locations.length > liveCount && (
              <span className="text-slate-400">· {locations.length - liveCount} offline</span>
            )}
          </div>
          <Button
            size="sm"
            variant={autoRefresh ? "default" : "outline"}
            onClick={() => setAutoRefresh((v) => !v)}
            className={autoRefresh ? "bg-[#0055FF] hover:bg-[#0040CC]" : ""}
            data-testid="map-toggle-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${autoRefresh ? "animate-spin-slow" : ""}`} />
            {autoRefresh ? "Auto-refresh on" : "Auto-refresh off"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              load();
              if (selectedId) loadTrail(selectedId);
            }}
            data-testid="map-refresh-now"
          >
            Refresh now
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-3 border-slate-200 shadow-none overflow-hidden">
          <CardContent className="p-0">
            <div
              className="relative w-full h-[540px]"
              data-testid="map-canvas"
            >
              <MapContainer
                center={center}
                zoom={13}
                scrollWheelZoom
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxZoom={19}
                />
                <AutoFit points={allPoints} />
                <FlyTo center={flyTarget} zoom={16} />

                {trailLatLngs.length > 1 && (
                  <Polyline
                    positions={trailLatLngs}
                    pathOptions={{ color: "#0055FF", weight: 4, opacity: 0.75 }}
                  />
                )}
                {trail.length > 0 && (
                  <Circle
                    center={[trail[0].lat, trail[0].lng]}
                    radius={40}
                    pathOptions={{
                      color: "#94a3b8",
                      fillColor: "#94a3b8",
                      fillOpacity: 0.5,
                    }}
                  />
                )}

                {locations.map((l) => {
                  const isSelected = l.employee_id === selectedId;
                  const live = isLive(l);
                  return (
                    <Fragment key={l.employee_id}>
                      {live && l.accuracy && l.accuracy <= 1000 ? (
                        <Circle
                          center={[l.lat, l.lng]}
                          radius={l.accuracy}
                          pathOptions={{
                            color: isSelected ? "#FF5000" : "#0055FF",
                            weight: 1,
                            fillOpacity: 0.08,
                          }}
                        />
                      ) : null}
                      <Marker
                        position={[l.lat, l.lng]}
                        icon={pinIcon(
                          (l.employee_name || "?").slice(0, 1).toUpperCase(),
                          isSelected ? "#FF5000" : live ? "#0055FF" : OFFLINE
                        )}
                        zIndexOffset={isSelected ? 1000 : live ? 500 : 0}
                        eventHandlers={{
                          click: () => focusOn(l),
                        }}
                      >
                        <Popup>
                          <div className="text-sm">
                            <div className="font-semibold">{l.employee_name}</div>
                            <div className="mono text-[11px] text-slate-500">
                              {l.lat.toFixed(5)}, {l.lng.toFixed(5)}
                            </div>
                            {l.accuracy ? (
                              <div className="mono text-[11px] text-slate-500">
                                ±{Math.round(l.accuracy)} m
                              </div>
                            ) : null}
                            <div className="mono text-[11px] text-slate-500">
                              {live ? "Updated" : "Offline · last seen"} {fmtAgo(l.captured_at)}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    </Fragment>
                  );
                })}
              </MapContainer>

              {locations.length === 0 && (
                <div className="absolute inset-0 grid place-items-center bg-white/70 text-sm text-slate-500 pointer-events-none">
                  No live locations yet — employees will appear as soon as they
                  open the mobile app.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none">
          <CardHeader className="border-b">
            <CardTitle className="font-heading text-lg">
              Team on the ground
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y">
            {locations.map((l) => {
              const live = isLive(l);
              return (
              <button
                key={l.employee_id}
                onClick={() => focusOn(l)}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 ${
                  selectedId === l.employee_id ? "bg-blue-50" : "hover:bg-slate-50"
                }`}
                data-testid={`map-list-${l.employee_id}`}
              >
                <div
                  className={`w-8 h-8 rounded-full text-white grid place-items-center font-heading font-bold text-xs ${
                    live ? "bg-[#0055FF]" : "bg-slate-300"
                  }`}
                >
                  {(l.employee_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {l.employee_name}
                  </div>
                  <div className="mono text-[11px] text-slate-500 truncate">
                    {l.lat.toFixed(4)}, {l.lng.toFixed(4)}
                    {l.accuracy ? (
                      <span className={l.accuracy > ROUGH_M ? "text-amber-600" : ""}>
                        {` · ±${Math.round(l.accuracy)}m`}
                        {l.accuracy > ROUGH_M ? " rough" : ""}
                      </span>
                    ) : null}
                  </div>
                  <div className={`mono text-[10px] ${live ? "text-emerald-600" : "text-slate-400"}`}>
                    {live ? `● live · ${fmtAgo(l.captured_at)}` : `offline · last seen ${fmtAgo(l.captured_at)}`}
                  </div>
                </div>
                <MapPin className="w-4 h-4 text-slate-400" />
              </button>
              );
            })}
            {locations.length === 0 && (
              <div className="p-6 text-sm text-slate-500">
                No locations reported yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {selected && (
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <Navigation className="w-3 h-3" /> Selected
              </div>
              <div className="font-heading text-lg mt-1">
                {selected.employee_name}
              </div>
              <div className="mono text-[11px] text-slate-500">
                {selected.lat.toFixed(5)}, {selected.lng.toFixed(5)}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-3 w-full"
                onClick={openInGoogleMaps}
                data-testid="map-open-gmaps"
              >
                <Locate className="w-3.5 h-3.5 mr-1" /> Open in Google Maps
              </Button>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <Route className="w-3 h-3" /> Trail
              </div>
              <div className="font-heading text-lg mt-1">
                {trail.length} points
              </div>
              <div className="mono text-[11px] text-slate-500">
                {totalKm.toFixed(2)} km travelled
              </div>
              <div className="mono text-[10px] text-slate-400 mt-2">
                Blue line on map = last 200 points.
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
                Accuracy
              </div>
              <div className="font-heading text-lg mt-1">
                {selected.accuracy ? `±${Math.round(selected.accuracy)} m` : "—"}
              </div>
              <div className="mono text-[11px] text-slate-500">
                {selected.accuracy > ROUGH_M
                  ? "Rough position from Wi-Fi/mobile towers, not GPS. Ask them to turn on Location (high accuracy)."
                  : `Reported by ${selected.employee_name}'s device.`}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
